import React, { createContext, useContext, useState, useEffect } from 'react';

const CookieConsentContext = createContext();

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('Hook must be used within the right Provider');
  }
  return context;
};

export const CookieConsentProvider = ({ children }) => {
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // immer true
    analytics: null, // null = keine Entscheidung, true = akzeptiert, false = abgelehnt
  });
  const [showBanner, setShowBanner] = useState(false);

  // Prüfen, ob bereits eine Entscheidung vorliegt
  useEffect(() => {
    const savedPreferences = localStorage.getItem('cookie-consent');
    if (savedPreferences) {
      const parsed = JSON.parse(savedPreferences);
      setCookiePreferences(parsed);
      setShowBanner(false);

      // Nur Matomo aktivieren, OHNE das Event erneut zu senden
      if (parsed.analytics) {
        enableMatomoSilently();
      }
    } else {
      // Keine Entscheidung → Banner anzeigen
      setShowBanner(true);
    }
  }, []);

  /**
   * Matomo aktivieren, ohne Event zu senden (für bereits gegebene Zustimmung)
   */
  const enableMatomoSilently = () => {
    window._paq = window._paq || [];
    // Nur den Cookie-Consent setzen, kein Event senden
    window._paq.push(['rememberCookieConsentGiven']);
  };

  /**
   * Consent im Tracker & Tag Manager aktivieren (MIT Event)
   */
  const enableMatomo = () => {
    window._paq = window._paq || [];
    window._mtm = window._mtm || [];

    // remember cookie consent was given for all subsequent page views and visits
    window._paq.push(['rememberCookieConsentGiven']);

    // Tag Manager: Event für Consent geben (nur bei neuer Zustimmung)
    window._mtm.push({ event: 'trackingConsentGiven' });
  };

  /**
   *  Matomo blockieren & Cookies löschen
   */
  const disableMatomo = () => {
    window._paq = window._paq || [];

    // Tracker: Consent zurücksetzen & Cookies blockieren
    window._paq.push(['forgetCookieConsentGiven']);
  };

  /**
   * User akzeptiert alle Cookies
   */
  const acceptAllCookies = () => {
    const newPreferences = { necessary: true, analytics: true };
    setCookiePreferences(newPreferences);
    localStorage.setItem('cookie-consent', JSON.stringify(newPreferences));
    setShowBanner(false);

    // Hier das Event senden, da es eine neue Zustimmung ist
    enableMatomo();
  };

  /**
   *  User lehnt Analytics-Cookies ab
   */
  const rejectAnalyticsCookies = () => {
    const newPreferences = { necessary: true, analytics: false };
    setCookiePreferences(newPreferences);
    localStorage.setItem('cookie-consent', JSON.stringify(newPreferences));
    setShowBanner(false);

    disableMatomo();
  };

  /**
   *  Preferences gezielt ändern (z. B. aus Datenschutz-Einstellungen)
   */
  const updatePreferences = newPreferences => {
    const currentAnalytics = cookiePreferences.analytics;
    const newAnalytics = newPreferences.analytics;

    setCookiePreferences(newPreferences);
    localStorage.setItem('cookie-consent', JSON.stringify(newPreferences));

    // Nur Event senden, wenn sich der Analytics-Status von false/null zu true ändert
    if (newAnalytics && currentAnalytics !== true) {
      enableMatomo(); // Mit Event
    } else if (newAnalytics) {
      enableMatomoSilently(); // Ohne Event
    } else {
      disableMatomo();
    }
  };

  /**
   *  Consent zurücksetzen (Banner erneut anzeigen)
   */
  const resetConsent = () => {
    localStorage.removeItem('cookie-consent');
    setCookiePreferences({ necessary: true, analytics: null });
    setShowBanner(true);
  };

  return (
    <CookieConsentContext.Provider
      value={{
        cookiePreferences,
        showBanner,
        acceptAllCookies,
        rejectAnalyticsCookies,
        updatePreferences,
        resetConsent,
        setShowBanner,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};
